<template>
    <section class="ko-tatou-dashboard">
        <q-banner v-if="loadError" rounded class="bg-red-1 text-negative q-mb-md">
            {{ loadError }}
        </q-banner>

        <q-table
            class="ko-tatou-table"
            flat
            bordered
            :rows="responses"
            :columns="columns"
            row-key="id"
            :loading="loading"
            :pagination="pagination"
            no-data-label="No Ko Tātou responses were found."
            @row-click="openResponse"
        >
            <template v-slot:body-cell-submitted_at="props">
                <q-td :props="props">{{ formatDate(props.row.submitted_at) }}</q-td>
            </template>

            <template v-slot:body-cell-person="props">
                <q-td :props="props">{{ props.row.person || 'Anonymous' }}</q-td>
            </template>

            <template v-slot:body-cell-age="props">
                <q-td :props="props">{{ formatAge(props.row.age) }}</q-td>
            </template>

            <template v-slot:body-cell-gender="props">
                <q-td :props="props">{{ props.row.gender || '-' }}</q-td>
            </template>

            <template v-slot:body-cell-actions="props">
                <q-td :props="props">
                    <q-btn
                        flat
                        dense
                        no-caps
                        color="primary"
                        icon="o_visibility"
                        label="View response"
                        @click.stop="openResponse(null, props.row)"
                    />
                </q-td>
            </template>
        </q-table>

        <q-dialog
            v-model="showResponseDialog"
            persistent
            class="engagement-form-dialog-shell"
            @hide="handleResponseDialogHide"
        >
            <q-card class="engagement-form-dialog">
                <q-card-section class="engagement-form-dialog__header">
                    <h2>Form Response</h2>
                    <q-btn flat round dense icon="close" v-close-popup>
                        <q-tooltip class="text-body2">Close</q-tooltip>
                    </q-btn>
                </q-card-section>
                <q-card-section class="engagement-form-dialog__body">
                    <DynamicFormRenderer
                        v-if="selectedResponse"
                        :form-id="formId"
                        :subject="selectedSubject"
                        :_responseId="selectedResponse.id"
                        @exit="onResponseClosed"
                        @form-submitted="onFormSubmitted"
                    />
                </q-card-section>
            </q-card>
        </q-dialog>
    </section>
</template>

<script>
import api from "@/services/api/api";
import DynamicFormRenderer from "@/components/DynamicFormComponents/DynamicFormRenderer.vue";

const AGE_LABELS = Object.freeze({
    under_16: "Under 16",
    "16_17": "16-17",
    "18_24": "18-24",
    "25_65": "25-65",
    "65_plus": "65+",
});

export default {
    name: "KoTatouDashboard",

    components: {
        DynamicFormRenderer,
    },

    props: {
        formId: {
            type: [Number, String],
            required: true,
        },
    },

    data() {
        return {
            responses: [],
            loading: false,
            loadError: null,
            selectedResponse: null,
            showResponseDialog: false,
            pagination: {
                sortBy: "submitted_at",
                descending: true,
                rowsPerPage: 20,
            },
            columns: [
                { name: "submitted_at", label: "Submission date", field: "submitted_at", align: "left", sortable: true },
                { name: "person", label: "Person", field: "person", align: "left", sortable: true },
                { name: "age", label: "Age", field: "age", align: "left", sortable: true },
                { name: "gender", label: "Gender", field: "gender", align: "left", sortable: true },
                { name: "actions", label: "", field: "actions", align: "right", sortable: false },
            ],
        };
    },

    computed: {
        selectedSubject() {
            return {
                source: "anonymous",
                id: null,
                name: this.selectedResponse?.person || "Anonymous",
            };
        },
    },

    created() {
        this.loadResponses();
    },

    methods: {
        async loadResponses() {
            this.loading = true;
            this.loadError = null;

            try {
                const response = await api.forms.getKoTatouResponses();
                this.responses = Array.isArray(response.data) ? response.data : [];
            } catch (error) {
                this.responses = [];
                this.loadError = "Failed to load Ko Tātou responses.";
            } finally {
                this.loading = false;
            }
        },

        openResponse(event, row) {
            if (!row?.id) return;

            this.selectedResponse = row;
            this.showResponseDialog = true;
        },

        async handleResponseDialogHide() {
            this.selectedResponse = null;
            await this.loadResponses();
        },

        onResponseClosed() {
            this.showResponseDialog = false;
        },

        onFormSubmitted() {
            this.showResponseDialog = false;
        },

        formatDate(dateString) {
            if (!dateString) return "-";
            const date = new Date(dateString);
            return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString("en-nz");
        },

        formatAge(age) {
            if (!age) return "-";
            return AGE_LABELS[age] || age;
        },
    },
};
</script>

<style scoped>
.ko-tatou-dashboard {
    padding: 20px;
}

.ko-tatou-table :deep(tbody tr) {
    cursor: pointer;
}

.engagement-form-dialog {
    display: flex;
    flex-direction: column;
    width: min(1000px, 98vw);
    max-height: calc(100vh - 40px);
    overflow: hidden;
}

.engagement-form-dialog__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 18px 16px 10px;
}

.engagement-form-dialog__header h2 {
    margin: 0;
    color: #25434a;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.25;
}

.engagement-form-dialog__body {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    padding: 12px 32px 32px;
}

.engagement-form-dialog__body :deep(.dynamic-form-page) {
    width: 100%;
    max-width: none;
}

.engagement-form-dialog__body :deep(.dynamic-form-page-content) {
    width: 100%;
}

@media (max-width: 700px) {
    .ko-tatou-dashboard {
        padding: 16px 12px;
    }
}
</style>

<style>
@media (min-width: 600px) {
    .engagement-form-dialog-shell.q-dialog__inner--minimized > div,
    .engagement-form-dialog-shell .q-dialog__inner--minimized > div {
        max-width: 98vw;
    }
}
</style>
